import React from 'react';

import './elementCatalogEmpty.css';
import { useShopWords } from '../../commerce/shopWords';

// The three things both catalog rows need and neither owns: how many across, the shape of what is
// coming while the shop is being read, and the one line a merchant is told when a row has nothing to
// draw. A shopper is told none of it — an empty row on a live page is simply an empty row.

/** The widest and narrowest a row may be laid out at, matching what the catalog offers; one suits a narrow column. */
const MIN_COLUMNS = 1;
const MAX_COLUMNS = 4;

/** Writes the chosen count into the grid, so the same stylesheet folds every row the same way. */
export const columnsStyle = (columns: number): React.CSSProperties =>
  ({ '--xe-cat-columns': String(Math.min(MAX_COLUMNS, Math.max(MIN_COLUMNS, Math.trunc(columns)))) } as React.CSSProperties);

/**
 * The shape of the cards that are coming, drawn while the shop is being read.
 *
 * A skeleton and not a spinner: these rows are content, and a spinner over content is what makes a
 * page jump when the content lands under it. Each block is the card's own aspect, so nothing moves.
 */
export const CatalogSkeleton: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: Math.min(Math.max(count, 1), 8) }, (_unused, at) => (
      <div key={at} className="xe-cat-skeleton" aria-hidden="true" />
    ))}
  </>
);

// Says why a row is empty, and only inside the builder — a shopper cannot act on any of these answers.
export const CatalogNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="xe-cat-note">{children}</p>
);

// Tells a shopper why a grid has nothing in it, and gives them the one way on: the whole catalogue.
export const CatalogEmpty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="xe-cat-empty" role="status">
    <p>{children}</p>
    <a href="/collections/all">{useShopWords().browseAll}</a>
  </div>
);
